import React, { useState, memo } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  UserProfile,
  UserProfileCertificates,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { ReactComponent as BinIcon } from "@web-src/images/icons/delete.svg";
import format from "date-fns/format";
import { isoToLocalDate } from "@web-src/utils/helper";
import { AvatarSelect } from "@jugl-web/ui-components/web/AvatarSelect";
import { useTranslations } from "@jugl-web/utils";
import { DatePicker } from "@jugl-web/ui-components/cross-platform/DatePicker";
import {
  InteractiveContainer,
  Popover,
  Button,
  Divider,
  LoadingAnimation,
  FormControlLabel,
  TextField,
  ConfirmationPopup,
} from "@jugl-web/ui-components/cross-platform";
import { Accordion } from "@jugl-web/ui-components/cross-platform/Accordion";
import { ReactComponent as CertificateIcon } from "./assets/certificate.svg";
import { useProfileUpdateToast } from "../../hooks/useProfileUpdateToast";
import { DateButton } from "../DateButton/DateButton";

type Inputs = {
  name_certification: string;
  completion: Date | undefined;
  expires: Date | undefined;
  issued: string;
};

const schema = yup.object().shape({
  name_certification: yup.string().required(),
  completion: yup.date().required(),
  expires: yup.date().required(),
  issued: yup.string().required(),
});

const EditCertificationsFormContainer = memo(() => {
  const { usersApi } = useRestApiProvider();
  const { data } = usersApi.useGetUserProfileQuery({});
  return <EditCertificationsFormComponent {...{ data }} />;
});

const EditCertificationsFormComponent: React.FC<{
  data: UserProfile | undefined;
}> = ({ data }) => {
  const { t } = useTranslations();
  return (
    <div className="w-1/2">
      {!data ? (
        <LoadingAnimation />
      ) : data.certificates.length === 0 ? (
        <div className="pt-10">
          <span className="pl-5 font-medium">
            {t({
              id: "certifications-form-page.add-certification",
              defaultMessage: "Add Certification",
            })}
          </span>
          <EditCertificationsForm type="add" />
        </div>
      ) : (
        <EditCertificationsList {...{ data }} />
      )}
    </div>
  );
};

const EditCertificationsList: React.FC<{ data: UserProfile }> = ({ data }) => {
  const [displayForm, setDisplayForm] = useState(false);
  const { t } = useTranslations();
  return (
    <div className="pt-5">
      <span className="pl-5 font-medium">
        {t({
          id: "certifications-form-page.you-certification",
          defaultMessage: "You Certification",
        })}
      </span>
      {data.certificates.map((el, idx) => (
        <>
          <EditCertificationsListItem key={el.id} data={el} />
          {data.certificates.length - 1 > idx && <Divider />}
        </>
      ))}
      {!displayForm && (
        <Button
          className="ml-5 mt-5 mb-10 w-1/2"
          variant="outlined"
          onClick={() => setDisplayForm(true)}
        >
          {t({
            id: "form-controls.add-new-certification.button",
            defaultMessage: "Add New Certification",
          })}
        </Button>
      )}
      {displayForm && (
        <>
          <span className="ml-5 font-medium">
            {t({
              id: "certifications-form-page.add-new-certification",
              defaultMessage: "Add New Certification",
            })}
          </span>
          <EditCertificationsForm
            type="add"
            onSuccess={() => setDisplayForm(false)}
          />
        </>
      )}
    </div>
  );
};

const EditCertificationsListItem: React.FC<{
  data: UserProfileCertificates;
}> = ({ data }) => {
  const [openModal, setOpenModal] = useState(false);
  const { usersApi } = useRestApiProvider();
  const [deleteCertification, { isLoading }] =
    usersApi.useDeleteUserProfileSectionMutation();
  const { showProfileUpdatedSnackbar } = useProfileUpdateToast();
  const { t } = useTranslations();

  const handleDeleteCertification = async () => {
    const res = await deleteCertification({
      type: "certificate",
      item_id: data.id,
    });
    if ("data" in res) {
      showProfileUpdatedSnackbar();
      setOpenModal(false);
    }
  };

  return (
    <>
      <Accordion
        variant="web"
        title={
          <div className="flex w-full items-center gap-5 px-5 py-3">
            {data.img ? (
              <img
                src={data.img}
                className="h-9 w-9 rounded-full"
                alt="Certificate"
              />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[rgba(238,90,138,0.1)]">
                <CertificateIcon />
              </div>
            )}
            <div className="flex grow flex-col gap-1">
              <span>{data.name}</span>
              <span className="text-grey text-sm">{`${format(
                isoToLocalDate(data.completion),
                "MMMM/yyyy"
              )} - ${format(
                isoToLocalDate(data.expires_on),
                "MMMM/yyyy"
              )}`}</span>
            </div>
            <Button
              color="tertiary"
              className="text-sm font-medium uppercase"
              isDisabled={isLoading}
              onClick={(e) => {
                e.stopPropagation();
                setOpenModal(true);
              }}
              variant="text"
              iconStart={<BinIcon />}
            >
              {t({
                id: "common.delete",
                defaultMessage: "Delete",
              })}
            </Button>
          </div>
        }
        className="flex-row-reverse px-4"
      >
        {({ onClose }) => (
          <EditCertificationsForm
            {...{ data }}
            type="update"
            onSuccess={onClose}
          />
        )}
      </Accordion>
      {openModal && (
        <ConfirmationPopup
          isOpen={openModal}
          onRequestClose={() => setOpenModal(false)}
          hasCancelButton
          message={t({
            id: "certifications-form-page.certification-information-removal-confirmation",
            defaultMessage:
              "Do you really want to remove Certification information?",
          })}
          variant="web"
          title={t({
            id: "delete-modal.delete-confirmation-title",
            defaultMessage: "Delete Confirmation",
          })}
          buttons={[
            {
              label: t({
                id: "common.delete",
                defaultMessage: "Delete",
              }),
              color: "tertiary",
              onClick: handleDeleteCertification,
              isDisabled: isLoading,
            },
          ]}
        />
      )}
    </>
  );
};

const EditCertificationsForm: React.FC<{
  data?: UserProfileCertificates;
  type: "add" | "update";
  onSuccess?: () => void;
}> = ({ data, type, onSuccess }) => {
  const { usersApi } = useRestApiProvider();
  const [addCertification, { isLoading }] =
    usersApi.useAddUserProfileSectionMutation();
  const [selectedAvatar, setSelectedAvatar] = useState<File>();
  const [updateCertification, { isLoading: isLoadingUpdate }] =
    usersApi.useUpdateUserProfileSectionMutation();
  const { showProfileUpdatedSnackbar } = useProfileUpdateToast();
  const { t } = useTranslations();
  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { isValid, isDirty },
  } = useForm<Inputs>({
    resolver: yupResolver(schema),
    values: {
      name_certification: data?.name || "",
      completion: data?.completion ? new Date(data.completion) : undefined,
      expires: data?.expires_on ? new Date(data.expires_on) : undefined,
      issued: data?.issued_by || "",
    },
  });

  const completionValue = watch("completion");
  const expiresValue = watch("expires");

  const onSubmit: SubmitHandler<Inputs> = async (params) => {
    const {
      name_certification: NameCertification,
      completion,
      expires,
      issued,
    } = { ...params };
    const dataForm = new FormData();
    dataForm.set("type", "certificate");
    dataForm.set("name", NameCertification);
    dataForm.set("issued_by", issued);
    dataForm.set("expires_on", expires?.toISOString() || "");
    dataForm.set("completion", completion?.toISOString() || "");
    if (selectedAvatar) dataForm.set("display_pic_file", selectedAvatar);
    let res;
    if (type === "add") {
      res = await addCertification({ data: dataForm });
    } else {
      res = await updateCertification({
        item_id: data?.id,
        data: dataForm,
      });
    }
    if ("data" in res) {
      showProfileUpdatedSnackbar();
      onSuccess?.();
    }
  };

  return (
    <div className="px-10 pb-10 pt-5">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-5">
          <AvatarSelect
            onSave={setSelectedAvatar}
            value={selectedAvatar}
            defaultSrc={data?.img || undefined}
            placeholder={<CertificateIcon className="h-10 w-10" />}
          />
        </div>
        <div className="flex w-full gap-[70px]">
          <div className="flex w-1/2 flex-col gap-12">
            <TextField
              isRequired
              label={t({
                id: "form-controls.certification-name.label",
                defaultMessage: "Name of Certification",
              })}
              isFullWidth
              placeholder={t({
                id: "form-controls.certification-name.placeholder",
                defaultMessage: "Name of the Certification",
              })}
              {...register("name_certification")}
            />
            <Controller
              name="expires"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Popover
                  className="w-[340px]"
                  placement="bottom-start"
                  renderTrigger={({ Trigger, triggerRef }) => (
                    <Trigger ref={triggerRef} as={InteractiveContainer}>
                      <FormControlLabel isRequired className="ml-2.5">
                        {t({
                          id: "form-controls.expires-on.label",
                          defaultMessage: "Expires On (Date)",
                        })}
                      </FormControlLabel>
                      <DateButton date={value} />
                    </Trigger>
                  )}
                >
                  {({ onClose }) => (
                    <DatePicker
                      minDate={completionValue}
                      onSubmit={onChange}
                      initialDate={value}
                      onClose={onClose}
                      className="p-4"
                      dateTransformation="endOfDay"
                    />
                  )}
                </Popover>
              )}
            />
            <Button
              isDisabled={!isValid || isLoading || isLoadingUpdate || !isDirty}
              fullWidth
              variant="contained"
              type="submit"
            >
              {t({
                id: "common.save",
                defaultMessage: "Save",
              })}
            </Button>
          </div>
          <div className="flex w-1/2 flex-col gap-12">
            <Controller
              name="completion"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Popover
                  className="w-[340px]"
                  placement="bottom-start"
                  renderTrigger={({ Trigger, triggerRef }) => (
                    <Trigger ref={triggerRef} as={InteractiveContainer}>
                      <FormControlLabel isRequired className="ml-2.5">
                        {t({
                          id: "form-controls.completion-date.label",
                          defaultMessage: "Completion (Date)",
                        })}
                      </FormControlLabel>
                      <DateButton date={value} />
                    </Trigger>
                  )}
                >
                  {({ onClose }) => (
                    <DatePicker
                      minDate={completionValue}
                      onSubmit={(date) => {
                        onChange(date);
                        if (expiresValue && date && date > expiresValue) {
                          setValue("expires", date && date, {
                            shouldValidate: true,
                          });
                        }
                      }}
                      initialDate={value}
                      onClose={onClose}
                      className="p-4"
                      dateTransformation="endOfDay"
                    />
                  )}
                </Popover>
              )}
            />
            <TextField
              isRequired
              label={t({
                id: "form-controls.issued-by.label",
                defaultMessage: "Issued By",
              })}
              isFullWidth
              placeholder={t({
                id: "form-controls.issued-by.placeholder",
                defaultMessage: "Issued By",
              })}
              {...register("issued")}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditCertificationsFormContainer;
